// src/features/leases/LeaseSummaryTab.jsx
//
// LEASES-FORKLIFT-2 — lightweight Lease tab for the office Location detail page.
//
// Previous version had a full inline detail view (1 000+ lines) with
// AccordionFieldPanels, ActivitySections, ConcessionCards, DocumentRows,
// CriticalDateRows, RecordProvenanceBar, LeaseTimeline, LeasesAtLocationCard,
// and LeaseTabTopSection. All of that heavy inline UI has been removed.
//
// New shape:
//   1. LeaseTabHeroBar — location-level aggregate KPIs (total rent, blended
//      rate, next expiry) across all leases. Unchanged component; allowances
//      not passed here (detail lives on the lease page).
//   2. LeaseReferenceCard — one compact card per lease, showing status pill,
//      lease type, term remaining, annual rent, next decision date, and a
//      "View lease →" link to /leases/:leaseId.
//   3. Empty state — "No lease data yet for this location." + optional
//      "Create draft lease" button (canWrite + onCreateDraft only). Unchanged.
//
// Exported helpers (sourceFor, computeBreakNoticeDl, autoRenewalValue,
// autoRenewalSource, buildDecisionsItems) are kept so sibling components
// (LeaseSummaryCard, FinancialTermsCard, upcomingMarkers.js) keep working
// without import changes.

import React, { useCallback, useEffect, useState } from "react";
import { T } from "../../app/tokens.js";
import { getActiveLeaseSummary, listLocationLeases } from "../../data/leasesClient.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { log } from "../../utils/logger.js";
import { useFxRates } from "../../data/useFxRates.js";

import { LeaseTabHeroBar } from "./components/LeaseTabHeroBar.jsx";
import { LeaseReferenceCard } from "./components/LeaseReferenceCard.jsx";
import { CurrencyToggle } from "./components/CurrencyToggle.jsx";
import { EditLeaseModal } from "./EditLeaseModal.jsx";
import { isSentinelEndDate, parseDateOnly } from "./leaseTabConfig.js";
import { getUpcomingMarkers } from "./upcomingMarkers.js";

// ─── Exported helpers (kept for sibling-component compat) ──────────────────

// Lookup a field's source from lease.fieldSources, falling back to
// 'spacebase' for fields delivered via the CSV import (last_csv_imported_at
// populated) and undefined otherwise.
export function sourceFor(lease, field) {
  const s = lease.fieldSources?.[field];
  if (s) return s;
  if (lease.lastCsvImportedAt && lease[field] != null) return "spacebase";
  return undefined;
}

// Compute the break notice deadline by subtracting breakNoticeMonths from
// breakDate. Returns an ISO date string (YYYY-MM-DD) or null.
export function computeBreakNoticeDl(breakDate, breakNoticeMonths) {
  if (!breakDate || breakNoticeMonths == null) return null;
  const dt = parseDateOnly(breakDate);
  if (!dt) return null;
  const dl = new Date(dt);
  dl.setMonth(dl.getMonth() - Number(breakNoticeMonths));
  return dl.toISOString().slice(0, 10);
}

// "Auto-Renewal" display value driven by noticePeriodText presence.
export function autoRenewalValue(noticePeriodText) {
  return noticePeriodText != null ? "Yes (recurring lease)" : null;
}

// Source for Auto-Renewal display: delegate to the underlying text field.
export function autoRenewalSource(lease) {
  if (lease.noticePeriodText == null) return undefined;
  return sourceFor(lease, "noticePeriodText") || "spacebase";
}

// ─── buildDecisionsItems (kept for upcomingMarkers consumers / unit tests) ──

function daysFrom(today, dateStr) {
  if (!dateStr || isSentinelEndDate(dateStr)) return null;
  const dt = parseDateOnly(dateStr);
  if (!dt) return null;
  return Math.ceil((dt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export function buildDecisionsItems(lease, today, criticalDates) {
  const DECISION_BUCKETS = new Set([
    "renewal_notice", "break_option", "expiration",
    "escalation", "deposit_burndown",
    "allowance_deadline", "allowance_warning", "allowance_expiry",
    "cam_audit", "other",
  ]);
  const markers = getUpcomingMarkers(lease, criticalDates);
  const items = [];
  markers.forEach((m) => {
    if (!DECISION_BUCKETS.has(m.type)) return;
    const d = daysFrom(today, m.dueDate);
    if (d == null || d < 0) return;
    const label = m.decisionLabel
      || (m.type === "other"
        ? (m.description || m.category_raw)
        : (m.category_raw || m.description))
      || (m.type !== "other" ? m.type : null);
    if (!label) return;
    items.push({ label, date: m.dueDate, daysToExpiry: d, criticalDateId: m.id, description: m.description ?? null, type: m.type });
  });
  items.sort((a, b) => a.daysToExpiry - b.daysToExpiry);
  return items;
}

// ─── LeaseSummaryTab ──────────────────────────────────────────────────────────

export function LeaseSummaryTab({
  locationId,
  canWrite,
  isAdmin = false,
  locationCity = null,
  // Lifted to LocationDetail; only passed here to control EditLeaseModal (unused
  // in the new lightweight tab but kept for props-compat with LocationDetail).
  leaseEditOpen = false,
  onLeaseEditChange = () => {},
  onCreateDraft = null,
  overrideLeaseId = null,
  onLeaseEditSaved = null,
  externalReload = 0,
}) {
  // primaryLease: rich object (with computed / yearCosts) from getActiveLeaseSummary.
  //   undefined  = still loading
  //   null       = location has no lease → show empty state
  //   object     = at least one lease exists
  const [primaryLease, setPrimaryLease] = useState(undefined);
  // allLeases: lightweight list from listLocationLeases — used for the card list.
  const [allLeases, setAllLeases] = useState([]);
  // Track list-fetch separately so empty state waits for both fetches.
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const { rates } = useFxRates();

  // ── Display currency toggle — session-only, resets on locationId change ──────
  const [displayCcy, setDisplayCcy] = useState("USD");
  useEffect(() => { setDisplayCcy("USD"); }, [locationId]);

  const refetch = useCallback(() => setReload((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setPrimaryLease(undefined);
    setListLoading(true);
    setError(null);

    getActiveLeaseSummary(locationId)
      .then((lease) => { if (!cancelled) setPrimaryLease(lease ?? null); })
      .catch((err) => { if (!cancelled) setError(err); });

    listLocationLeases(locationId)
      .then((leases) => {
        if (!cancelled) {
          setAllLeases(leases || []);
          setListLoading(false);
        }
      })
      .catch((err) => {
        log.error("LeaseSummaryTab list-leases fetch failed", { locationId, err: String(err) });
        if (!cancelled) {
          setAllLeases([]);
          setListLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [locationId, reload, externalReload]);

  // ── Loading / error ─────────────────────────────────────────────────────────

  if (error) {
    return <ErrorBanner message={error.message || "Failed to load lease."} />;
  }

  // Wait for both fetches before deciding on empty state.
  if (primaryLease === undefined || listLoading) {
    return (
      <div style={{ padding: 20, color: T.mid, fontSize: T.fsBase }}>
        Loading lease…
      </div>
    );
  }

  // ── Empty state ──────────────────────────────────────────────────────────────
  // Key off the list (allLeases) as the authoritative "no leases" signal.
  // getActiveLeaseSummary can return null when only non-active leases exist,
  // but those non-active leases should still be shown as cards.

  if (allLeases.length === 0) {
    return (
      <div style={{ padding: 30, textAlign: "center", color: T.mid, fontSize: T.fsBase }}>
        <div style={{ fontSize: T.fsXl6, marginBottom: 8 }}>📄</div>
        <div style={{ fontWeight: T.fwSemibold, color: T.dark, marginBottom: 4 }}>
          No lease data yet for this location.
        </div>
        <div style={{ marginBottom: canWrite ? 16 : 0 }}>
          Fill in whatever terms are known to start tracking this lease.
        </div>
        {canWrite && onCreateDraft && (
          <button
            type="button"
            data-testid="create-draft-lease-button"
            onClick={onCreateDraft}
            style={{
              background: T.blue,
              color: T.white,
              border: "none",
              padding: "8px 18px",
              borderRadius: 6,
              fontSize: T.fsBase,
              fontWeight: T.fwBold,
              cursor: "pointer",
            }}
          >
            Create draft lease
          </button>
        )}
      </div>
    );
  }

  // ── Loaded: hero bar + per-lease cards ──────────────────────────────────────
  //
  // Enrich allLeases with the primaryLease computed block so the hero bar's
  // annualRent / blendedRate KPIs populate (listLocationLeases omits computed;
  // getActiveLeaseSummary includes it for the primary).
  //
  // primaryLease may be null when only non-active leases exist (e.g. all expired).
  // In that case allLeases is still non-empty so we got past the empty-state guard.
  // Each lease carries annualRentUsd from listLocationLeases (resolveCurrentYearRentUsd);
  // LeaseReferenceCard uses that directly — never totalCost ÷ term.

  const heroLeases = primaryLease
    ? allLeases.map((l) =>
        l.id === primaryLease.id ? { ...l, computed: primaryLease.computed, rentSchedule: primaryLease.rentSchedule, yearCosts: primaryLease.yearCosts } : l
      )
    : allLeases;

  const nativeCcy = heroLeases[0]?.currency || primaryLease?.currency || "USD";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "0 0 16px" }}>
      {/* Currency toggle header — shown above the hero bar. */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <CurrencyToggle
          nativeCurrency={nativeCcy}
          value={displayCcy}
          onChange={setDisplayCcy}
        />
      </div>
      {/* Location-level aggregate KPIs — total rent, blended rate, next expiry.
          Allowances are not fetched here; detail lives on the lease detail page.
          displayCcy state drives conversion; rates from useFxRates. */}
      <LeaseTabHeroBar
        leases={heroLeases}
        allowances={[]}
        displayCcy={displayCcy}
        rates={rates}
      />

      {/* One compact summary card per lease */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {heroLeases.map((lease) => (
          <LeaseReferenceCard key={lease.id} lease={lease} />
        ))}
      </div>

      {/* EditLeaseModal — kept at tab level so it has access to refetch.
          State (leaseEditOpen / onLeaseEditChange) is lifted to LocationDetail. */}
      {leaseEditOpen && primaryLease && (
        <EditLeaseModal
          isOpen={leaseEditOpen}
          onClose={() => onLeaseEditChange(false)}
          lease={primaryLease}
          canWrite={canWrite}
          isAdmin={isAdmin}
          onSaved={(updatedLease) => {
            refetch();
            onLeaseEditSaved?.(updatedLease);
          }}
          onDeleted={() => {
            onLeaseEditChange(false);
            refetch();
            onLeaseEditSaved?.(null);
          }}
        />
      )}
    </div>
  );
}
